import Skeleton from '../../Atoms/Skeleton'
import Typography from '../../Atoms/Typography'
import useLogRocket from '../../Hooks/useLogRocket'
import { useUserStore } from '../../Stores/UserStore/UserStore'
import styles from './TopVehiclesGrid.module.scss'
import vehicleDataForTopVehiclesGrid from './VehicleDataForTopVehiclesGrid'

import React, { useEffect, useRef, useState } from 'react'

import Image from 'next/image'
import PropTypes from 'prop-types'

const AUDIENCE_STREET = 'street'
const AUDIENCE_DEFAULT = 'default'
const AUDIENCE_RACE = 'race'

const SkeletonItem = () => (
  <div className={styles.gridItem}>
    <Skeleton fullHeight fullWidth />
  </div>
)

const TopVehiclesGrid = ({ audience, isLoading }) => {
  const [shuffledVehicles, setShuffledVehicles] = useState([])
  const [showHintScroll, setShowHintScroll] = useState(false)
  const gridRef = useRef(null)
  const logRocketInitialized = useUserStore((x) => x.context.logRocketInitialized)
  const { logRocketTrack } = useLogRocket(logRocketInitialized)

  useEffect(() => {
    const selectedVehicles = vehicleDataForTopVehiclesGrid[audience === AUDIENCE_DEFAULT ? AUDIENCE_STREET : audience]
    const vehicleEntries = Object.entries(selectedVehicles)
    const shuffled = vehicleEntries.sort(() => Math.random() - 0.5).slice(0, 6)
    setShuffledVehicles(shuffled)

    // Check if it's a mobile device
    const isMobile = window?.innerWidth <= 768

    if (isMobile) {
      // Add the hint scroll class after a short delay
      const timer = setTimeout(() => {
        setShowHintScroll(true)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [audience])

  useEffect(() => {
    if (showHintScroll) {
      // Remove the hint scroll class after the animation completes
      const timer = setTimeout(() => {
        setShowHintScroll(false)
      }, 1500) // Match this with the animation duration

      return () => clearTimeout(timer)
    }
  }, [showHintScroll])

  const skeletonCount = 6
  const showSkeleton = isLoading === true

  function GetTitleText() {
    let titleText

    if (audience === AUDIENCE_STREET || audience === AUDIENCE_DEFAULT) {
      titleText = 'Shop Popular Models'
    } else if (audience === AUDIENCE_RACE) {
      titleText = 'Shop Popular Race Types'
    }
    return titleText
  }

  return (
    <div className={styles.container}>
      <Typography as="h2" font="bold" size={24} className={styles.title} dataTesting="top-vehicles-grid-title">
        {showSkeleton ? <Skeleton height={48} width={250} /> : GetTitleText()}
      </Typography>

      <div className={styles.gridWrapper}>
        <div ref={gridRef} className={`${styles.grid} ${showHintScroll ? styles.scrollHint : ''}`}>
          {showSkeleton
            ? Array(skeletonCount)
                .fill()
                .map((_, index) => <SkeletonItem key={index} />)
            : shuffledVehicles.map(([vehicleName, vehicleData]) => (
                <div key={vehicleName} className={styles.gridItem}>
                  <div className={styles.categoryInfo}>
                    <Typography as="div" font="bold" size={16} className={styles.categoryTitle}>
                      {vehicleName}
                    </Typography>
                  </div>
                  <a href={vehicleData.shopUrl} className={styles.imagePlaceholder}>
                    <div className={styles.imageContainer}>
                      <Image
                        className={styles.image}
                        src={vehicleData.imageUrl}
                        alt={`${vehicleName} image`}
                        fill={true}
                        style={{ objectFit: 'cover' }}
                        onError={(e) => {
                          console.error(`Error loading image for ${vehicleName}:`, e)
                        }}
                      />
                    </div>
                  </a>
                </div>
              ))}
        </div>
      </div>
    </div>
  )
}

TopVehiclesGrid.propTypes = {
  audience: PropTypes.oneOf([AUDIENCE_DEFAULT, AUDIENCE_RACE, AUDIENCE_STREET]),
  isLoading: PropTypes.bool,
}

TopVehiclesGrid.defaultProps = {
  audience: AUDIENCE_STREET,
}

export default TopVehiclesGrid
